import { TICK_DURATION } from "@/config/constants";
import { ResourceType } from "@/game/entities/config/resources";
import { revealSettlementByOffer } from "@/game/OfferActions";
import useTileRepository from "@/hooks/useTileRepository";
import useUserStore from "@/store/useUserStore";
import type { ConversionOffer } from "@/types/shop";
import { OfferType } from "@/types/ui";

export interface Offer {
	id: number;
	value: Partial<Record<ResourceType, number | (() => number)>> | null;
	price: Partial<Record<ResourceType, number>>;
	header?: string;
	description?: string;
	replaceValue?: string | number;
	timeLimit?: number | null; // seconds
	ribbonInfo?: string | null;
	isBought?: boolean;
	limit?: {
		current: number;
		max: number;
	} | null;
	type: OfferType;
	iconName?: string;
	callback?: () => void;
	condition?: (() => boolean) | null;
	conditionForNotAvailability?: (() => boolean) | null;
	dynamicDescription?: (() => string) | null;
}

const SUPER_CLICKER_OFFERS: Map<number, Offer> = new Map([
	[
		1,
		{
			id: 1,
			type: OfferType.SUPER_CLICKER,
			value: { [ResourceType.SUPER_CLICKER]: 80 },
			price: { [ResourceType.GEMS]: 200 },
			description: `resource.${ResourceType.SUPER_CLICKER}_description`,
		},
	],
	[
		2,
		{
			id: 2,
			type: OfferType.SUPER_CLICKER,
			value: { [ResourceType.SUPER_CLICKER]: 500 },
			price: { [ResourceType.GEMS]: 1_000 },
			description: `resource.${ResourceType.SUPER_CLICKER}_description`,
		},
	],
]);

const AD_SKIPPER_OFFERS: Map<number, Offer> = new Map([
	[
		1,
		{
			id: 1,
			type: OfferType.AD_SKIPPER,
			value: { [ResourceType.AD_SKIPPER]: 20 },
			price: { [ResourceType.GEMS]: 500 },
			description: `resource.${ResourceType.AD_SKIPPER}_description`,
		},
	],
	[
		2,
		{
			id: 2,
			type: OfferType.AD_SKIPPER,
			value: { [ResourceType.AD_SKIPPER]: 100 },
			price: { [ResourceType.GEMS]: 2000 },
			description: `resource.${ResourceType.AD_SKIPPER}_description`,
		},
	],
]);

const GEM_FOR_COINS_OFFERS: Map<number, Offer> = new Map([
	[
		1,
		{
			id: 1,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 10 },
			price: { [ResourceType.COINS]: 25_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		2,
		{
			id: 2,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 15 },
			price: { [ResourceType.COINS]: 50_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		3,
		{
			id: 3,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 20 },
			price: { [ResourceType.COINS]: 100_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		4,
		{
			id: 4,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 25 },
			price: { [ResourceType.COINS]: 200_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		5,
		{
			id: 5,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 30 },
			price: { [ResourceType.COINS]: 400_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		6,
		{
			id: 6,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 35 },
			price: { [ResourceType.COINS]: 800_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		7,
		{
			id: 7,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 40 },
			price: { [ResourceType.COINS]: 1_500_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		8,
		{
			id: 8,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 45 },
			price: { [ResourceType.COINS]: 2_500_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		9,
		{
			id: 9,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 50 },
			price: { [ResourceType.COINS]: 5_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		10,
		{
			id: 10,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 55 },
			price: { [ResourceType.COINS]: 10_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		11,
		{
			id: 11,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 60 },
			price: { [ResourceType.COINS]: 20_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		12,
		{
			id: 12,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 65 },
			price: { [ResourceType.COINS]: 40_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		13,
		{
			id: 13,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 70 },
			price: { [ResourceType.COINS]: 80_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		14,
		{
			id: 14,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 75 },
			price: { [ResourceType.COINS]: 160_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		15,
		{
			id: 15,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 80 },
			price: { [ResourceType.COINS]: 320_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		16,
		{
			id: 16,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 85 },
			price: { [ResourceType.COINS]: 500_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		17,
		{
			id: 17,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 90 },
			price: { [ResourceType.COINS]: 750_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		18,
		{
			id: 18,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 95 },
			price: { [ResourceType.COINS]: 1_000_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		19,
		{
			id: 19,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 100 },
			price: { [ResourceType.COINS]: 1_300_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		20,
		{
			id: 20,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 105 },
			price: { [ResourceType.COINS]: 1_700_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		21,
		{
			id: 21,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 110 },
			price: { [ResourceType.COINS]: 2_200_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		22,
		{
			id: 22,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 115 },
			price: { [ResourceType.COINS]: 2_800_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		23,
		{
			id: 23,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 120 },
			price: { [ResourceType.COINS]: 3_500_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		24,
		{
			id: 24,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 125 },
			price: { [ResourceType.COINS]: 4_500_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
	[
		25,
		{
			id: 25,
			type: OfferType.GEM_FOR_COINS,
			value: { [ResourceType.GEMS]: 130 },
			price: { [ResourceType.COINS]: 6_000_000_000 },
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
]);

const TICKS_PER_MIN = 60 / TICK_DURATION;

const COINS_FOR_GEMS_OFFERS: Map<number, Offer> = new Map([
	[
		1,
		{
			id: 1,
			type: OfferType.COINS_FOR_GEMS,
			value: {
				[ResourceType.COINS]: (income: number): number => {
					// 15 min income for gems
					return Math.trunc(income * (15 * TICKS_PER_MIN));
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 150 },
		},
	],
	[
		2,
		{
			id: 2,
			type: OfferType.COINS_FOR_GEMS,
			value: {
				[ResourceType.COINS]: (income: number): number =>
					// 60 min income for gems
					Math.trunc(income * (60 * TICKS_PER_MIN)),
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 500 },
		},
	],
]);

const RESOURCES_FOR_GEMS_OFFERS: Map<number, Offer> = new Map([
	[
		1,
		{
			id: 1,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.LUMBER]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Lumber Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 150 },
		},
	],
	[
		2,
		{
			id: 2,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.STONE]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Stone Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 175 },
		},
	],
	[
		3,
		{
			id: 3,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.CLAY]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Clay Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 200 },
		},
	],
	[
		4,
		{
			id: 4,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.IRON]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Iron Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 225 },
		},
	],
	[
		5,
		{
			id: 5,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.GOODS]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Goods Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 250 },
		},
	],
	[
		6,
		{
			id: 6,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.STEEL]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Steel Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 275 },
		},
	],
	[
		7,
		{
			id: 7,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.ARMY]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Army Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 300 },
		},
	],
	[
		8,
		{
			id: 8,
			type: OfferType.RESOURCES_FOR_GEMS,
			value: {
				[ResourceType.RATIONS]: (
					storage: number,
					income: number,
				): number => {
					// NOTE: Storage * 0.05 + Rations Income * 180
					return Math.trunc(storage * 0.05 + income * 180);
				},
			} as Offer["value"],
			price: { [ResourceType.GEMS]: 325 },
		},
	],
]);

const ACTION_FOR_GEMS_OFFERS: Map<number, Offer> = new Map([
	[
		1,
		{
			id: 1,
			type: OfferType.ACTION_FOR_GEMS,
			value: null,
			price: { [ResourceType.GEMS]: 100 },
			header: "ui.show_undiscovered_town",
			iconName: "settlement-shield",
			callback: revealSettlementByOffer,
			condition: (): boolean => {
				return (
					useUserStore().revealedSettlementFromOfferCoordinates ===
					null
				);
			},
			conditionForNotAvailability: (): boolean => {
				// Returns true if there are no settlements left to discover
				return (
					useTileRepository().getSettlementCoordinatesOrderedByDistance()
						.length === 0
				);
			},
			dynamicDescription: (): string => {
				const { revealedSettlementFromOfferCoordinates: revealed } =
					useUserStore();
				const remainingCount =
					useTileRepository().getSettlementCoordinatesOrderedByDistance()
						.length;

				if (remainingCount === 0) {
					return "ui.all_towns_discovered";
				}

				return revealed !== null
					? "ui.show_undiscovered_town_limit"
					: "";
			},
		},
	],
]);

const SUPER_CLICKER_FREE: Map<number, ConversionOffer> = new Map([
	[
		1,
		{
			id: 1,
			type: OfferType.SUPER_CLICKER_FREE,
			value: { [ResourceType.SUPER_CLICKER]: 10 },
			price: null,
			description: "event.available_once_a_day",
			limit: {
				current: 0,
				max: 1,
			},
		},
	],
]);

export const GAME_CURRENCY_OFFERS_CONFIG = {
	SUPER_CLICKER_OFFERS,
	AD_SKIPPER_OFFERS,
	GEM_FOR_COINS_OFFERS,
	COINS_FOR_GEMS_OFFERS,
	RESOURCES_FOR_GEMS_OFFERS,
	ACTION_FOR_GEMS_OFFERS,
	SUPER_CLICKER_FREE,
} as const;
